import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from "react";

import { bumpVersion, CaptureUpdateAction, hashElementsVersion, reconcileElements, restoreElements } from "@excalidraw/excalidraw";
import { RemoteExcalidrawElement } from "@excalidraw/excalidraw/data/reconcile";
import { ElementsMapOrArray, ExcalidrawElement, OrderedExcalidrawElement } from "@excalidraw/excalidraw/element/types";
import { ExcalidrawImperativeAPI, SocketId } from "@excalidraw/excalidraw/types";
import * as Sentry from "@sentry/react";
import throttle from "lodash/throttle";
import { Socket } from "socket.io-client";

import { isSyncableElement, SyncableExcalidrawElement } from "./data";
import { ImageSynchronizer } from "./ImageSync";

// Performance optimization constants
const CURSOR_SYNC_TIMEOUT = 30; // Reduced from 50ms for smoother cursor updates
const POINTER_BATCH_INTERVAL = 30; // Batch pointer updates every 30ms
const IDLE_THRESHOLD = 30000;
const ACTIVE_THRESHOLD = 5000;
const COORDINATE_PRECISION = 1; // Round coordinates to 1 decimal place (reduces payload size)
const SYNC_FULL_SCENE_INTERVAL_MS = 20000;
// Throttle incremental broadcasts during active drawing.
const INCREMENTAL_BROADCAST_THROTTLE_MS = 60;

// Define user states
enum UserIdleState {
  ACTIVE = "active",
  IDLE = "idle",
  AWAY = "away"
}
interface CollabSocketIOProps {
  excalidrawAPI: ExcalidrawImperativeAPI;
  socket: Socket | null;
  lessonId: string | null;
  username?: string;
  userId: string;
  onViewModeUpdate?: (viewModeEnabled: boolean) => void;
}

export const CollabSocketIO = forwardRef<any, CollabSocketIOProps>(
  ({ excalidrawAPI, socket, lessonId, username, userId, onViewModeUpdate }, ref) => {
    const lastElementsRef = useRef<readonly OrderedExcalidrawElement[]>([]);
    const lastBroadcastedOrReceivedSceneVersion = useRef<number>(-1);
    const collaboratorsRef = useRef<Map<SocketId, any>>(new Map());
    const idleTimeoutId = useRef<number | null>(null);
    const activeIntervalId = useRef<number | null>(null);
    const lastPointerUpdateRef = useRef<number>(0);
    const imageSyncRef = useRef<ImageSynchronizer | null>(null);
    const hasSentInitialImages = useRef<boolean>(false);
    const socketIdRef = useRef<string | null>(null);
    const pendingImageElementsRef = useRef<Map<string, any[]>>(new Map());
    const broadcastedElementVersions = useRef<Map<string, number>>(new Map());

    // Pointer update batching for performance
    const pointerUpdateBufferRef = useRef<any[]>([]);
    const pointerBatchIntervalRef = useRef<number | null>(null);

    // Round coordinates to reduce payload size
    const roundCoordinate = (value: number): number => {
      return Math.round(value * Math.pow(10, COORDINATE_PRECISION)) / Math.pow(10, COORDINATE_PRECISION);
    };

    // Round pointer coordinates
    const roundPointer = (pointer: any): any => {
      if (!pointer) return pointer;
      return {
        ...pointer,
        x: pointer.x !== undefined ? roundCoordinate(pointer.x) : pointer.x,
        y: pointer.y !== undefined ? roundCoordinate(pointer.y) : pointer.y,
      };
    };

    const sendMessage = (event: string, data: unknown, volatile: boolean = false) => {
      if (!socket || !socket.connected) {
        Sentry.addBreadcrumb({
          category: "whiteboard",
          message: `Cannot send ${event}: socket not connected`,
          level: "warning",
        });
        return;
      }

      try {
        if (volatile) {
          socket.volatile.emit(event, data);
        } else {
          socket.emit(event, data);
        }
      } catch (error) {
        Sentry.captureException(error, {
          tags: { whiteboard_op: "send_message" },
          extra: { event },
        });
      }
    };

    // Batch and send pointer updates
    const flushPointerUpdates = useCallback(() => {
      if (pointerUpdateBufferRef.current.length === 0 || !socket || !socket.connected) {
        return;
      }

      // Send the most recent pointer update (most accurate position)
      const latestUpdate = pointerUpdateBufferRef.current[pointerUpdateBufferRef.current.length - 1];

      // Use volatile emit for cursor updates (can be dropped if network is busy)
      sendMessage("whiteboard:pointer-update", latestUpdate, true);

      // Clear the buffer
      pointerUpdateBufferRef.current = [];
    }, [socket]);

    useImperativeHandle(ref, () => ({
      onPointerUpdate: (pointerData: any) => {
        if (!socket || !socket.connected) return;

        const now = Date.now();
        if (now - lastPointerUpdateRef.current < CURSOR_SYNC_TIMEOUT) {
          return;
        }
        lastPointerUpdateRef.current = now;

        // Round pointer coordinates to reduce payload size
        const roundedPointer = roundPointer(pointerData.pointer);

        // Add to buffer instead of sending immediately
        pointerUpdateBufferRef.current.push({
          type: "pointer-update",
          socketId: socketIdRef.current || socket.id,
          pointer: roundedPointer,
          button: pointerData.button,
          username: username || "Unknown",
          userId,
          userState: UserIdleState.ACTIVE,
          tool: roundedPointer.tool
        });

        // Start batching interval if not already running
        if (!pointerBatchIntervalRef.current) {
          pointerBatchIntervalRef.current = window.setInterval(flushPointerUpdates, POINTER_BATCH_INTERVAL);
        }
      }
    }), [socket, username, userId, flushPointerUpdates]);

    // Handle user presence and idle state
    const onIdleStateChange = (userState: UserIdleState) => {
      if (!socket || !socket.connected) return;

      sendMessage("whiteboard:idle-status", {
        type: "idle-status",
        socketId: socketIdRef.current || socket.id,
        userState,
        username: username || "Unknown",
        userId,
      });
    };

    const reportIdle = () => {
      onIdleStateChange(UserIdleState.IDLE);
      if (activeIntervalId.current) {
        window.clearInterval(activeIntervalId.current);
        activeIntervalId.current = null;
      }
    };

    const reportActive = () => {
      onIdleStateChange(UserIdleState.ACTIVE);
    };

    const onPointerMove = () => {
      if (idleTimeoutId.current) {
        window.clearTimeout(idleTimeoutId.current);
        idleTimeoutId.current = null;
      }

      idleTimeoutId.current = window.setTimeout(reportIdle, IDLE_THRESHOLD);

      if (!activeIntervalId.current) {
        activeIntervalId.current = window.setInterval(reportActive, ACTIVE_THRESHOLD);
      }
    };

    const onVisibilityChange = () => {
      if (document.hidden) {
        if (idleTimeoutId.current) {
          window.clearTimeout(idleTimeoutId.current);
          idleTimeoutId.current = null;
        }
        if (activeIntervalId.current) {
          window.clearInterval(activeIntervalId.current);
          activeIntervalId.current = null;
        }
        onIdleStateChange(UserIdleState.AWAY);
      } else {
        idleTimeoutId.current = window.setTimeout(reportIdle, IDLE_THRESHOLD);
        activeIntervalId.current = window.setInterval(reportActive, ACTIVE_THRESHOLD);
        onIdleStateChange(UserIdleState.ACTIVE);
      }
    };

    // Initialize idle detector
    useEffect(() => {
      document.addEventListener("pointermove", onPointerMove);
      document.addEventListener("visibilitychange", onVisibilityChange);

      return () => {
        document.removeEventListener("pointermove", onPointerMove);
        document.removeEventListener("visibilitychange", onVisibilityChange);
        if (idleTimeoutId.current) {
          window.clearTimeout(idleTimeoutId.current);
        }
        if (activeIntervalId.current) {
          window.clearInterval(activeIntervalId.current);
        }
      };
    }, []);

    // Handle socket connection and message handlers
    useEffect(() => {
      if (!socket || !excalidrawAPI || !lessonId) {
        return;
      }

      // Store socket ID
      socketIdRef.current = socket.id || null;

      Sentry.setContext("whiteboard", {
        lessonId,
        userId,
        socketId: socket.id,
      });
      Sentry.addBreadcrumb({
        category: "whiteboard",
        message: "Whiteboard session initialized",
        level: "info",
        data: {
          lessonId,
          userId,
          socketId: socket.id
        },
      });

      // Initialize image synchronizer
      // The ImageSynchronizer callback receives a JSON string, so we parse it and send via Socket.IO
      imageSyncRef.current = new ImageSynchronizer(
        excalidrawAPI,
        (message: string) => {
          try {
            const data = JSON.parse(message);
            // Send the parsed data directly via Socket.IO
            if (socket && socket.connected) {
              socket.emit("whiteboard:image-file-update", data);
            }
          } catch (error) {
            Sentry.captureException(error, {
              tags: { whiteboard_op: "image_sync_send" },
              extra: { lessonId },
            });
          }
        }
      );

      // Send all existing images when connected
      const sendAllExistingImages = () => {
        if (!imageSyncRef.current || hasSentInitialImages.current || !socket || !socket.connected) {
          return;
        }

        const elements = excalidrawAPI.getSceneElementsIncludingDeleted();
        const imageElements = elements.filter(
          (element) => element.type === "image" && !element.isDeleted
        );

        imageElements.forEach((element) => {
          if (element.type === "image" && element.fileId) {
            const files = excalidrawAPI.getFiles();
            let fileData;
            if (files && typeof files === 'object') {
              fileData = (files as any)[element.fileId];
            }

            if (fileData) {
              // Use the image synchronizer to send, which handles the proper format
              const fileMessage = JSON.stringify({
                type: "image-file-update",
                fileId: element.fileId,
                fileData: fileData
              });

              // Use the image synchronizer's sendMessage callback
              try {
                const data = JSON.parse(fileMessage);
                socket.emit("whiteboard:image-file-update", data);
              } catch (error) {
                Sentry.captureException(error, {
                  tags: { whiteboard_op: "initial_image_send" },
                  extra: {
                    lessonId,
                    fileId: element.type === "image" ? element.fileId : undefined
                  },
                });
              }
            }
          }
        });

        hasSentInitialImages.current = true;
      };

      // Function to send images when ready
      const checkAndSendImages = () => {
        if (socket && socket.connected && socketIdRef.current && !hasSentInitialImages.current) {
          sendAllExistingImages();
        } else if (!hasSentInitialImages.current) {
          // Retry after a short delay if not ready (max 5 seconds)
          const retryCount = (checkAndSendImages as any).retryCount || 0;
          if (retryCount < 10) {
            (checkAndSendImages as any).retryCount = retryCount + 1;
            setTimeout(checkAndSendImages, 500);
          } else {
            Sentry.logger.warn("Whiteboard initial image sync failed after retries", {
              lessonId: lessonId ?? "",
              userId,
              retryCount: 10,
            });
          }
        }
      };

      // Listen for socket connection to send images
      const onSocketConnect = () => {
        setTimeout(checkAndSendImages, 1000);
      };

      // If already connected, send images immediately
      if (socket.connected) {
        setTimeout(checkAndSendImages, 1500);
      } else {
        // Wait for connection
        socket.once("connect", onSocketConnect);
      }

      const imageTimeout = setTimeout(() => {
        if (!hasSentInitialImages.current) {
          checkAndSendImages();
        }
      }, 2000);

      // Handle whiteboard updates from other users
      const handleWhiteboardUpdate = (data: any) => {
        try {
          if (data.type === "whiteboard-update" && excalidrawAPI) {
            Sentry.startSpan(
              {
                name: "whiteboard.reconcile",
                op: "whiteboard"
              },
              () => {
                let remoteElements = data.elements;
                const appState = excalidrawAPI.getAppState();
                const existingElements = excalidrawAPI.getSceneElementsIncludingDeleted();

                // NOTE ideally we restore _after_ reconciliation but we can't do that
                // as we'd regenerate even elements such as appState.newElement which would
                // break the state.
                //
                // IMPORTANT: snapshot versions BEFORE restoreElements. Internally,
                // restoreElements calls syncInvalidIndices which calls mutateElement
                // to assign the `index` ordering property. mutateElement unconditionally
                // does `element.version++` whenever any property changes. Since remote
                // elements never carry `index`, every element's version gets inflated by
                // +1, which corrupts version-based reconciliation (reconcileElements and
                // bumpElementVersions both rely on these versions). We pin them back
                // immediately after restore so reconciliation sees the true semantic versions.
                const remoteVersionSnapshot = new Map<string, { version: number; versionNonce: number }>(
                  (data.elements as ExcalidrawElement[]).map((el: ExcalidrawElement) => [
                    el.id,
                    {
                      version: el.version ?? 1,
                      versionNonce: el.versionNonce ?? 0
                    },
                  ])
                );
                remoteElements = restoreElements(remoteElements, null);
                remoteElements = remoteElements.map((el: ExcalidrawElement) => {
                  const snap = remoteVersionSnapshot.get(el.id);
                  if (snap) {
                    return {
                      ...el,
                      version: snap.version,
                      versionNonce: snap.versionNonce
                    };
                  }
                  return el;
                });

                // Check for image elements that need file data BEFORE reconciling
                if (imageSyncRef.current) {
                  const imageElements = remoteElements.filter(
                    (element: ExcalidrawElement) => element.type === "image" && !element.isDeleted && element.fileId
                  );

                  // Group elements by fileId
                  const elementsByFileId = new Map<string, any[]>();
                  imageElements.forEach((element: ExcalidrawElement) => {
                    if (element.type === "image" && element.fileId) {
                      const fileId = element.fileId;
                      if (!elementsByFileId.has(fileId)) {
                        elementsByFileId.set(fileId, []);
                      }
                      elementsByFileId.get(fileId)!.push(element);

                      const files = excalidrawAPI.getFiles();
                      let fileData;
                      if (files && typeof files === 'object') {
                        fileData = (files as any)[fileId];
                      }

                      if (!fileData) {
                        if (!pendingImageElementsRef.current.has(fileId)) {
                          pendingImageElementsRef.current.set(fileId, []);
                        }
                        pendingImageElementsRef.current.get(fileId)!.push(element);
                      }
                    }
                  });
                }

                let reconciledElements = reconcileElements(
                  existingElements,
                  remoteElements,
                  appState,
                );

                reconciledElements = bumpElementVersions(
                  reconciledElements,
                  existingElements,
                );

                // Filter out image elements that don't have file data yet
                const elementsToApply = reconciledElements.filter((element) => {
                  if (element.type === "image" && element.fileId) {
                    const files = excalidrawAPI.getFiles();
                    let fileData;
                    if (files && typeof files === 'object') {
                      fileData = (files as any)[element.fileId];
                    }
                    if (!fileData) {
                      return false;
                    }
                  }
                  return true;
                });

                excalidrawAPI.updateScene({
                  elements: elementsToApply,
                  captureUpdate: CaptureUpdateAction.NEVER,
                  appState: {
                    ...excalidrawAPI.getAppState(),
                    collaborators: collaboratorsRef.current
                  }
                });

                // Hash AFTER updateScene so it matches what handleChange will see,
                // preventing the broadcast echo loop
                const postUpdateElements = excalidrawAPI.getSceneElementsIncludingDeleted();
                const version = hashElementsVersion(postUpdateElements);
                lastBroadcastedOrReceivedSceneVersion.current = version;

                lastElementsRef.current = postUpdateElements;
              },
            );
          }
        } catch (error) {
          Sentry.captureException(error, {
            tags: { whiteboard_op: "handle_update" },
            extra: {
              lessonId,
              remoteElementCount: data.elements?.length,
              collaboratorCount: collaboratorsRef.current.size,
            },
          });
        }
      };

      // Handle pointer updates
      const handlePointerUpdate = (data: any) => {
        try {
          if (data.type === "pointer-update" && excalidrawAPI) {
            const collaborators = new Map(collaboratorsRef.current);
            const collabKey = data.userId as SocketId;
            if (!collabKey) return;
            const currentCollaborator = collaborators.get(collabKey) || {};

            // Round received pointer coordinates for consistency
            const roundedPointer = roundPointer(data.pointer);

            const isCurrentUser = data.socketId === socketIdRef.current;

            collaborators.set(collabKey, {
              ...currentCollaborator,
              socketId: data.socketId,
              pointer: roundedPointer,
              button: data.button,
              selectedElementIds: data.selectedElementIds,
              username: data.username,
              userState: data.userState,
              tool: data.tool,
              isCurrentUser,
              lastActiveTool: data.tool,
              activeTool: data.tool
            });
            collaboratorsRef.current = collaborators;

            excalidrawAPI.updateScene({
              appState: {
                ...excalidrawAPI.getAppState(),
                collaborators
              }
            });
          }
        } catch (error) {
          Sentry.captureException(error, { tags: { whiteboard_op: "pointer_update" } });
        }
      };

      // Handle idle status updates
      const handleIdleStatus = (data: any) => {
        try {
          if (data.type === "idle-status" && excalidrawAPI) {
            const collaborators = new Map(collaboratorsRef.current);
            const collabKey = data.userId as SocketId;
            if (!collabKey) return;
            const existingCollaborator = collaborators.get(collabKey);
            if (existingCollaborator) {
              collaborators.set(collabKey, {
                ...existingCollaborator,
                socketId: data.socketId,
                userState: data.userState
              });
              collaboratorsRef.current = collaborators;

              excalidrawAPI.updateScene({
                appState: {
                  ...excalidrawAPI.getAppState(),
                  collaborators
                }
              });
            }
          }
        } catch (error) {
          Sentry.captureException(error, { tags: { whiteboard_op: "idle_status" } });
        }
      };

      // Handle view mode updates
      const handleViewModeUpdate = (data: any) => {
        try {
          if (data.type === "view-mode-update" && excalidrawAPI) {
            const currentAppState = excalidrawAPI.getAppState();
            excalidrawAPI.updateScene({
              appState: {
                ...currentAppState,
                viewModeEnabled: data.viewModeEnabled
              }
            });
            onViewModeUpdate?.(data.viewModeEnabled);
          }
        } catch (error) {
          Sentry.captureException(error, { tags: { whiteboard_op: "view_mode_update" } });
        }
      };

      // Handle erase all
      const handleEraseAll = (data: any) => {
        try {
          if (data.type === "erase-all" && excalidrawAPI) {
            const currentAppState = excalidrawAPI.getAppState();
            excalidrawAPI.updateScene({
              elements: [],
              appState: currentAppState
            });
          }
        } catch (error) {
          Sentry.captureException(error, { tags: { whiteboard_op: "erase_all" } });
        }
      };

      // Handle image file updates
      const handleImageFileUpdate = (data: any) => {
        try {
          if (data.type === "image-file-update" && imageSyncRef.current && excalidrawAPI) {
            const { fileId, fileData } = data;
            if (fileId && fileData) {
              // Add the file to Excalidraw
              imageSyncRef.current.handleImageFileUpdate(fileId, fileData);

              // Check if there are pending elements waiting for this file
              const pendingElements = pendingImageElementsRef.current.get(fileId);
              if (pendingElements && pendingElements.length > 0) {
                // Get current elements and reconcile with pending ones
                const currentElements = excalidrawAPI.getSceneElementsIncludingDeleted();
                const reconciledElements = reconcileElements(
                  currentElements,
                  pendingElements as RemoteExcalidrawElement[],
                  excalidrawAPI.getAppState()
                );

                // Update the scene with the reconciled elements now that we have the file
                excalidrawAPI.updateScene({
                  elements: reconciledElements,
                  appState: {
                    ...excalidrawAPI.getAppState(),
                    collaborators: collaboratorsRef.current
                  }
                });

                const postImageElements = excalidrawAPI.getSceneElementsIncludingDeleted();
                lastBroadcastedOrReceivedSceneVersion.current = hashElementsVersion(postImageElements);
                lastElementsRef.current = postImageElements;

                // Clear pending elements for this file
                pendingImageElementsRef.current.delete(fileId);
              }
            } else {
              Sentry.logger.warn("Whiteboard image update missing fileId or fileData", {
                lessonId: lessonId ?? "",
                hasFileId: String(!!data.fileId),
                hasFileData: String(!!data.fileData),
              });
            }
          }
        } catch (error) {
          Sentry.captureException(error, {
            tags: { whiteboard_op: "image_file_update" },
            extra: {
              lessonId,
              fileId: data?.fileId
            },
          });
        }
      };

      // Handle user joined
      const handleUserJoined = (_data: any) => {
        Sentry.addBreadcrumb({
          category: "whiteboard",
          message: "User joined whiteboard",
          level: "info",
          data: {
            joinedUserId: _data?.userId,
            elementCount: excalidrawAPI?.getSceneElementsIncludingDeleted()?.length,
            collaboratorCount: collaboratorsRef.current.size,
          },
        });

        // Send existing images to new user when they join
        if (socket && socket.connected) {
          setTimeout(() => {
            sendAllExistingImages();
          }, 500);

          //immediately sync the whiteboard to the new user
          broadcastElements(
            excalidrawAPI.getSceneElementsIncludingDeleted(),
            true,
          );
          lastBroadcastedOrReceivedSceneVersion.current = hashElementsVersion(excalidrawAPI.getSceneElementsIncludingDeleted());
        }
      };

      // Handle participants list (received when we join)
      const handleParticipants = (data: any) => {
        if (excalidrawAPI && data?.participants && Array.isArray(data.participants)) {
          const validSocketIds = new Set(
            data.participants.map((p: any) => p.socketId)
          );
          const collaborators = new Map(collaboratorsRef.current);
          let pruned = false;

          for (const [key, collab] of collaborators) {
            if (collab.socketId && !validSocketIds.has(collab.socketId)) {
              collaborators.delete(key);
              pruned = true;
            }
          }

          if (pruned) {
            collaboratorsRef.current = collaborators;
            excalidrawAPI.updateScene({
              appState: {
                ...excalidrawAPI.getAppState(),
                collaborators
              }
            });
          }
        }

        // When we receive participants, we're fully joined - send images
        if (socket && socket.connected && !hasSentInitialImages.current) {
          setTimeout(() => {
            sendAllExistingImages();
          }, 500);
        }
      };

      // Handle user left
      const handleUserLeft = (data: any) => {
        if (!data.userId) return;

        Sentry.addBreadcrumb({
          category: "whiteboard",
          message: "User left whiteboard",
          level: "info",
          data: {
            leftUserId: data.userId,
            collaboratorCount: collaboratorsRef.current.size - 1
          },
        });

        const collaborators = new Map(collaboratorsRef.current);
        collaborators.delete(data.userId as SocketId);
        collaboratorsRef.current = collaborators;

        excalidrawAPI?.updateScene({
          appState: {
            ...excalidrawAPI.getAppState(),
            collaborators
          }
        });
      };

      // Register event listeners
      socket.on("whiteboard:update", handleWhiteboardUpdate);
      socket.on("whiteboard:pointer-update", handlePointerUpdate);
      socket.on("whiteboard:idle-status", handleIdleStatus);
      socket.on("whiteboard:view-mode-update", handleViewModeUpdate);
      socket.on("whiteboard:erase-all", handleEraseAll);
      socket.on("whiteboard:image-file-update", handleImageFileUpdate);
      socket.on("whiteboard:user-joined", handleUserJoined);
      socket.on("whiteboard:user-left", handleUserLeft);
      socket.on("whiteboard:participants", handleParticipants);

      return () => {
        clearTimeout(imageTimeout);
        socket.off("connect", onSocketConnect);
        socket.off("whiteboard:update", handleWhiteboardUpdate);
        socket.off("whiteboard:pointer-update", handlePointerUpdate);
        socket.off("whiteboard:idle-status", handleIdleStatus);
        socket.off("whiteboard:view-mode-update", handleViewModeUpdate);
        socket.off("whiteboard:erase-all", handleEraseAll);
        socket.off("whiteboard:image-file-update", handleImageFileUpdate);
        socket.off("whiteboard:user-joined", handleUserJoined);
        socket.off("whiteboard:user-left", handleUserLeft);
        socket.off("whiteboard:participants", handleParticipants);
        imageSyncRef.current?.reset();
        hasSentInitialImages.current = false;

        // Clean up pointer batching
        if (pointerBatchIntervalRef.current) {
          window.clearInterval(pointerBatchIntervalRef.current);
          pointerBatchIntervalRef.current = null;
        }
        pointerUpdateBufferRef.current = [];
      };
    }, [socket, excalidrawAPI, lessonId, username, onViewModeUpdate]);

    // Optimize elements by removing unnecessary data
    const optimizeElements = useCallback((
      elements: readonly OrderedExcalidrawElement[],
      syncAll: boolean,
      activeDrawingId?: string | null,
    ): readonly OrderedExcalidrawElement[] => {
      // sync out only the elements we think we need to to save bandwidth.
      const syncableElements = elements.reduce((acc, element) => {
        const isDrawing = activeDrawingId && element.id === activeDrawingId;

        if (
          (syncAll ||
            isDrawing || // Always sync the element currently being drawn
            !broadcastedElementVersions.current.has(element.id) ||
            element.version > broadcastedElementVersions.current.get(element.id)!) &&
          (isDrawing || isSyncableElement(element)) // Bypass size check if it's the active drawing
        ) {
          // Compress the payload by stripping redundant decimals
          const compressedElement: any = { ...element };
          compressedElement.x = roundCoordinate(element.x);
          compressedElement.y = roundCoordinate(element.y);
          if (element.width !== undefined) compressedElement.width = roundCoordinate(element.width);
          if (element.height !== undefined) compressedElement.height = roundCoordinate(element.height);

          if (element.type === "freedraw" && element.points) {
            compressedElement.points = element.points.map((p: readonly [number, number]) => [
              roundCoordinate(p[0]),
              roundCoordinate(p[1])
            ]);
          }

          acc.push(compressedElement as SyncableExcalidrawElement);
        }
        return acc;
      }, [] as SyncableExcalidrawElement[]);

      for (const syncableElement of syncableElements) {
        broadcastedElementVersions.current.set(
          syncableElement.id,
          syncableElement.version,
        );
      }
      return syncableElements;
    }, []);

    // Broadcast function
    const broadcastElements = useCallback(
      (
        elements: readonly OrderedExcalidrawElement[],
        syncAll: boolean = false,
        activeDrawingId?: string | null,
      ) => {
        if (!socket || !socket.connected) return;

        const version = hashElementsVersion(elements);
        // Skip if version hasn't changed (e.g., during panning)
        if (version === lastBroadcastedOrReceivedSceneVersion.current && !syncAll) {
          return;
        }

        Sentry.startSpan(
          {
            name: "whiteboard.broadcast",
            op: "whiteboard",
            attributes: {
              elementCount: elements.length,
              syncAll
            }
          },
          () => {
            if (imageSyncRef.current) {
              imageSyncRef.current.syncNewImages();
            }

            const optimizedElements = optimizeElements(elements, syncAll, activeDrawingId);

            const messageData = {
              type: "whiteboard-update",
              elements: optimizedElements,
            };

            sendMessage("whiteboard:update", messageData);

            lastElementsRef.current = optimizedElements;
          },
        );
      },
      [socket, excalidrawAPI, optimizeElements]
    );

    const queueBroadcastAllElements = useCallback(
      throttle(() => {
        broadcastElements(
          excalidrawAPI.getSceneElementsIncludingDeleted(),
          true,
        );
        lastBroadcastedOrReceivedSceneVersion.current = hashElementsVersion(excalidrawAPI.getSceneElementsIncludingDeleted());
      }, SYNC_FULL_SCENE_INTERVAL_MS),
      [excalidrawAPI]
    );

    // Handle whiteboard changes and pointer updates
    useEffect(() => {
      if (!excalidrawAPI || !socket) return;

      let lastHandleChangeVersion = -1;

      // Standard throttled broadcast for all changes
      const broadcastChange = throttle(() => {
        if (!socket.connected) return;

        const elements = excalidrawAPI.getSceneElementsIncludingDeleted();
        const appState = excalidrawAPI.getAppState();
        const activeDrawingElement = (appState as any).newElement as OrderedExcalidrawElement | null;

        let allElements = elements;
        if (activeDrawingElement && !elements.some(el => el.id === activeDrawingElement.id)) {
          allElements = [...elements, activeDrawingElement];
        }

        const version = hashElementsVersion(allElements);
        if (version === lastHandleChangeVersion) return;
        lastHandleChangeVersion = version;

        broadcastElements(allElements, false, activeDrawingElement?.id);
        lastBroadcastedOrReceivedSceneVersion.current = version;
        queueBroadcastAllElements();
      }, INCREMENTAL_BROADCAST_THROTTLE_MS, {
        leading: true,
        trailing: true
      });

      const handleChange = () => {
        if (!socket.connected) return;

        // This simple throttle handles both mid-stroke and commit correctly,
        // because optimizeElements always sends the activeDrawingId, and
        // Excalidraw's imperitive onChange fires often enough.
        broadcastChange();
      };

      // Register onChange handler
      const unsubscribeOnChange = excalidrawAPI.onChange(handleChange);

      // Send initial state when socket connects
      const sendInitialState = () => {
        if (socket.connected && excalidrawAPI) {
          const elements = excalidrawAPI.getSceneElementsIncludingDeleted();
          if (elements.length > 0) {
            const version = hashElementsVersion(elements);
            lastBroadcastedOrReceivedSceneVersion.current = -1;
            broadcastElements(elements, true);
            lastBroadcastedOrReceivedSceneVersion.current = version;
          }
        }
      };

      // Send initial state if already connected
      if (socket.connected) {
        setTimeout(sendInitialState, 500);
      }

      // Also listen for socket reconnection to ensure sync continues
      const handleReconnect = () => {
        socketIdRef.current = socket?.id ?? null;
        Sentry.addBreadcrumb({
          category: "whiteboard",
          message: "Socket reconnected, re-syncing state",
          level: "info",
          data: {
            lessonId,
            socketId: socket?.id,
            elementCount: excalidrawAPI?.getSceneElementsIncludingDeleted()?.length,
            collaboratorCount: collaboratorsRef.current.size,
          },
        });
        setTimeout(sendInitialState, 500);
      };

      socket.on("connect", handleReconnect);

      return () => {
        if (typeof unsubscribeOnChange === 'function') {
          unsubscribeOnChange();
        }

        broadcastChange.cancel();
        queueBroadcastAllElements.cancel();
        lastBroadcastedOrReceivedSceneVersion.current = -1;
        socket.off("connect", handleReconnect);

        // Clean up pointer batching interval
        if (pointerBatchIntervalRef.current) {
          window.clearInterval(pointerBatchIntervalRef.current);
          pointerBatchIntervalRef.current = null;
        }

        // Flush any pending pointer updates
        flushPointerUpdates();

        // Clean up by updating scene to remove collaborators
        if (excalidrawAPI) {
          excalidrawAPI.updateScene({
            appState: {
              ...excalidrawAPI.getAppState(),
              collaborators: new Map()
            }
          });
        }
      };
    }, [excalidrawAPI, socket, broadcastElements, flushPointerUpdates]);

    return null;
  }
);

CollabSocketIO.displayName = "CollabSocketIO";

export const bumpElementVersions = <T extends ExcalidrawElement>(
  targetElements: readonly T[],
  localElements: Readonly<ElementsMapOrArray> | null | undefined,
) => {
  const localElementsMap = localElements ? arrayToMap(localElements) : null;

  return targetElements.map((element) => {
    const localElement = localElementsMap?.get(element.id);

    if (
      localElement &&
      (localElement.version > element.version ||
        // same versions but different versionNonce means different edits
        // (this often means the element was bumped during restore e.g. due
        // to re-indexing, and the original element was modified elsewhere
        // and supplied as localElements)
        (localElement.version === element.version &&
          localElement.versionNonce !== element.versionNonce))
    ) {
      return bumpVersion(element, localElement.version);
    }
    return element;
  });
};

/**
 * Transforms array of objects containing `id` attribute,
 * or array of ids (strings), into a Map, keyd by `id`.
 */
export const arrayToMap = <T extends { id: string } | string>(
  items: readonly T[] | Map<string, T>,
) => {
  if (items instanceof Map) {
    return items;
  }
  return items.reduce((acc: Map<string, T>, element) => {
    acc.set(typeof element === "string" ? element : element.id, element);
    return acc;
  }, new Map() as Map<string, T>);
};
